import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Plus, Edit2, Trash2, X, Server, CheckCircle, AlertCircle, Wifi, WifiOff, Loader2, Shield } from 'lucide-react';
import { useAppContext, TLD, DomainRegistrar } from '../../context/AppContext';
export function DomainManager() {
  const {
    tlds,
    addTld,
    updateTld,
    deleteTld,
    domainRegistrars,
    addDomainRegistrar,
    updateDomainRegistrar,
    deleteDomainRegistrar
  } = useAppContext();
  const [activeTab, setActiveTab] = useState<'tlds' | 'registrars'>('tlds');
  const [tldCategoryFilter, setTldCategoryFilter] = useState<'all' | 'popular' | 'new' | 'country' | 'specialty'>('all');
  // Modals state
  const [isTldModalOpen, setIsTldModalOpen] = useState(false);
  const [editingTld, setEditingTld] = useState<TLD | null>(null);
  const [isRegModalOpen, setIsRegModalOpen] = useState(false);
  const [editingReg, setEditingReg] = useState<DomainRegistrar | null>(null);
  const [isTestingReg, setIsTestingReg] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  // TLD Handlers
  const filteredTlds = tlds.filter(t => tldCategoryFilter === 'all' || t.category === tldCategoryFilter);
  const handleEditTld = (tld: TLD) => {
    setEditingTld(tld);
    setIsTldModalOpen(true);
  };
  const handleDeleteTld = (id: string) => {
    if (window.confirm('Are you sure you want to delete this TLD?')) {
      deleteTld(id);
    }
  };
  const handleSaveTld = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const data: TLD = {
      id: editingTld ? editingTld.id : `tld_${Date.now()}`,
      extension: form.get('extension') as string,
      registrarId: form.get('registrarId') as string,
      registerPrice: parseFloat(form.get('registerPrice') as string),
      renewPrice: parseFloat(form.get('renewPrice') as string),
      transferPrice: parseFloat(form.get('transferPrice') as string),
      category: form.get('category') as TLD['category'],
      whoisPrivacy: form.get('whoisPrivacy') === 'on',
      active: form.get('active') === 'on'
    };
    // Ensure extension starts with dot
    if (!data.extension.startsWith('.')) {
      data.extension = '.' + data.extension;
    }
    if (editingTld) {
      updateTld(data.id, data);
    } else {
      addTld(data);
    }
    setIsTldModalOpen(false);
    setEditingTld(null);
  };
  // Registrar Handlers
  const handleEditReg = (reg: DomainRegistrar) => {
    setEditingReg(reg);
    setTestResult(null);
    setIsRegModalOpen(true);
  };
  const handleDeleteReg = (id: string) => {
    // Check if TLDs are using this registrar
    const inUse = tlds.some(t => t.registrarId === id);
    if (inUse) {
      alert('Cannot delete this registrar because it is linked to active TLDs. Reassign those TLDs first.');
      return;
    }
    if (window.confirm('Are you sure you want to remove this registrar connection?')) {
      deleteDomainRegistrar(id);
    }
  };
  const handleTestReg = () => {
    setIsTestingReg(true);
    setTestResult(null);
    setTimeout(() => {
      setIsTestingReg(false);
      setTestResult(Math.random() > 0.2 ? 'success' : 'error');
    }, 1500);
  };
  const handleSaveReg = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const supportedTldsStr = form.get('supportedTlds') as string;
    const supportedTlds = supportedTldsStr.split(',').map(s => s.trim()).filter(s => s);
    const data: DomainRegistrar = {
      id: editingReg ? editingReg.id : `reg_${Date.now()}`,
      name: form.get('name') as string,
      apiEndpoint: form.get('apiEndpoint') as string,
      apiKey: form.get('apiKey') as string,
      status: testResult === 'success' ? 'connected' : editingReg ? editingReg.status : 'disconnected',
      supportedTlds
    };
    if (editingReg) {
      updateDomainRegistrar(data.id, data);
    } else {
      addDomainRegistrar(data);
    }
    setIsRegModalOpen(false);
    setEditingReg(null);
  };
  return <div className="space-y-8" data-id="element-464">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-465">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-466">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-467">
            Domain Management
          </h1>
          <p className="text-slate-400" data-id="element-468">
            Manage TLD pricing and domain registrar API connections.
          </p>
        </motion.div>
        <div className="flex gap-3" data-id="element-469">
          {activeTab === 'tlds' ? <button onClick={() => {
          setEditingTld(null);
          setIsTldModalOpen(true);
        }} className="bg-fox-600 hover:bg-fox-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors" data-id="element-470">
              <Plus size={18} data-id="element-471" /> Add TLD
            </button> : <button onClick={() => {
          setEditingReg(null);
          setTestResult(null);
          setIsRegModalOpen(true);
        }} className="bg-fox-600 hover:bg-fox-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors" data-id="element-472">
              <Plus size={18} data-id="element-473" /> Add Registrar
            </button>}
        </div>
      </div>

      {/* Main Tabs */}
      <div className="flex border-b border-slate-800" data-id="element-474">
        <button onClick={() => setActiveTab('tlds')} className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${activeTab === 'tlds' ? 'border-fox-500 text-fox-500' : 'border-transparent text-slate-400 hover:text-slate-300'}`} data-id="element-475">
          TLD Pricing
        </button>
        <button onClick={() => setActiveTab('registrars')} className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${activeTab === 'registrars' ? 'border-fox-500 text-fox-500' : 'border-transparent text-slate-400 hover:text-slate-300'}`} data-id="element-476">
          Registrar Connections
        </button>
      </div>

      {/* TLDs Tab Content */}
      {activeTab === 'tlds' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-6" data-id="element-477">
          {/* Category Filters */}
          <div className="flex flex-wrap gap-2" data-id="element-478">
            {(['all', 'popular', 'new', 'country', 'specialty'] as const).map(cat => <button key={cat} onClick={() => setTldCategoryFilter(cat)} className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors border ${tldCategoryFilter === cat ? 'bg-fox-500/10 border-fox-500/20 text-fox-500' : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-800'}`} data-id="element-479">
                  {cat === 'all' ? 'All TLDs' : cat === 'new' ? 'New & Tech' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>)}
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden" data-id="element-480">
            <div className="overflow-x-auto" data-id="element-481">
              <table className="w-full text-left text-sm" data-id="element-482">
                <thead className="bg-slate-900/50 border-b border-slate-800 text-slate-400" data-id="element-483">
                  <tr data-id="element-484">
                    <th className="px-6 py-4 font-medium" data-id="element-485">Extension</th>
                    <th className="px-6 py-4 font-medium" data-id="element-486">Registrar</th>
                    <th className="px-6 py-4 font-medium" data-id="element-487">Register</th>
                    <th className="px-6 py-4 font-medium" data-id="element-488">Renew</th>
                    <th className="px-6 py-4 font-medium" data-id="element-489">Transfer</th>
                    <th className="px-6 py-4 font-medium" data-id="element-490">Status</th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-491">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50" data-id="element-492">
                  {filteredTlds.map(tld => {
                const registrar = domainRegistrars.find(r => r.id === tld.registrarId);
                return <tr key={tld.id} className="hover:bg-slate-900/50 transition-colors" data-id="element-493">
                        <td className="px-6 py-4" data-id="element-494">
                          <div className="font-bold text-white text-lg" data-id="element-495">
                            {tld.extension}
                          </div>
                          {tld.whoisPrivacy && <div className="text-[10px] text-emerald-500 flex items-center gap-1 mt-1" data-id="element-496">
                              <Shield size={10} data-id="element-497" /> Free Privacy
                            </div>}
                        </td>
                        <td className="px-6 py-4" data-id="element-498">
                          {registrar ? <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-slate-900 border border-slate-800 text-xs text-slate-300" data-id="element-499">
                              <Server size={12} className={registrar.status === 'connected' ? 'text-emerald-500' : 'text-red-500'} data-id="element-500" />
                              {registrar.name}
                            </span> : <span className="text-red-500 text-xs" data-id="element-501">
                              Unlinked
                            </span>}
                        </td>
                        <td className="px-6 py-4 font-medium text-white" data-id="element-502">
                          ${tld.registerPrice}
                        </td>
                        <td className="px-6 py-4 text-slate-400" data-id="element-503">
                          ${tld.renewPrice}
                        </td>
                        <td className="px-6 py-4 text-slate-400" data-id="element-504">
                          ${tld.transferPrice}
                        </td>
                        <td className="px-6 py-4" data-id="element-505">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border ${tld.active ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-slate-800 text-slate-400 border-slate-700'}`} data-id="element-506">
                            {tld.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-507">
                          <div className="flex items-center justify-end gap-2" data-id="element-508">
                            <button onClick={() => handleEditTld(tld)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-509">
                              <Edit2 size={16} data-id="element-510" />
                            </button>
                            <button onClick={() => handleDeleteTld(tld.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors" data-id="element-511">
                              <Trash2 size={16} data-id="element-512" />
                            </button>
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>}

      {/* Registrars Tab Content */}
      {activeTab === 'registrars' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-513">
          {domainRegistrars.map(reg => <div key={reg.id} className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-514">
              <div className="flex justify-between items-start mb-6" data-id="element-515">
                <div className="flex items-center gap-4" data-id="element-516">
                  <div className="w-12 h-12 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400" data-id="element-517">
                    <Globe size={24} data-id="element-518" />
                  </div>
                  <div data-id="element-519">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-520">
                      {reg.name}
                      {reg.status === 'connected' ? <CheckCircle size={14} className="text-emerald-500" data-id="element-521" /> : <AlertCircle size={14} className="text-red-500" data-id="element-522" />}
                    </h3>
                    <p className="text-slate-500 text-xs font-mono truncate max-w-[200px] sm:max-w-[300px]" data-id="element-523">
                      {reg.apiEndpoint}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6" data-id="element-524">
                <p className="text-xs text-slate-500 mb-2" data-id="element-525">
                  Supported TLDs ({reg.supportedTlds.length})
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-526">
                  {reg.supportedTlds.slice(0, 8).map(tld => <span key={tld} className="text-[10px] px-2 py-0.5 bg-slate-900 border border-slate-800 rounded text-slate-400" data-id="element-527">
                      {tld}
                    </span>)}
                  {reg.supportedTlds.length > 8 && <span className="text-[10px] px-2 py-0.5 bg-slate-900 border border-slate-800 rounded text-slate-500" data-id="element-528">
                      +{reg.supportedTlds.length - 8} more
                    </span>}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-800" data-id="element-529">
                <span className={`text-xs font-medium px-2 py-1 rounded ${reg.status === 'connected' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`} data-id="element-530">
                  {reg.status === 'connected' ? 'Connected' : 'Disconnected'}
                </span>
                <div className="flex gap-2" data-id="element-531">
                  <button onClick={() => handleEditReg(reg)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-532">
                    <Edit2 size={16} data-id="element-533" />
                  </button>
                  <button onClick={() => handleDeleteReg(reg.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors" data-id="element-534">
                    <Trash2 size={16} data-id="element-535" />
                  </button>
                </div>
              </div>
            </div>)}
        </motion.div>}

      {/* TLD Modal */}
      <AnimatePresence data-id="element-536">
        {isTldModalOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-537">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-538">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10" data-id="element-539">
                <h2 className="text-xl font-bold text-white" data-id="element-540">
                  {editingTld ? 'Edit TLD Pricing' : 'Add New TLD'}
                </h2>
                <button onClick={() => setIsTldModalOpen(false)} className="text-slate-400 hover:text-white" data-id="element-541">
                  <X size={24} data-id="element-542" />
                </button>
              </div>
              <form onSubmit={handleSaveTld} className="p-6 space-y-6" data-id="element-543">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-544">
                  <div className="space-y-2" data-id="element-545">
                    <label className="text-sm font-medium text-slate-300" data-id="element-546">
                      Extension *
                    </label>
                    <input name="extension" defaultValue={editingTld?.extension} required placeholder=".com" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-547" />
                  </div>
                  <div className="space-y-2" data-id="element-548">
                    <label className="text-sm font-medium text-slate-300" data-id="element-549">
                      Category *
                    </label>
                    <select name="category" defaultValue={editingTld?.category || 'popular'} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-550">
                      <option value="popular" data-id="element-551">Popular</option>
                      <option value="new" data-id="element-552">New & Tech</option>
                      <option value="country" data-id="element-553">Country Code (ccTLD)</option>
                      <option value="specialty" data-id="element-554">Specialty</option>
                    </select>
                  </div>
                  <div className="space-y-2 md:col-span-2" data-id="element-555">
                    <label className="text-sm font-medium text-slate-300" data-id="element-556">
                      Domain Registrar *
                    </label>
                    <select name="registrarId" required defaultValue={editingTld?.registrarId} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-557">
                      <option value="" data-id="element-558">Select a registrar...</option>
                      {domainRegistrars.map(reg => <option key={reg.id} value={reg.id} data-id="element-559">
                          {reg.name} ({reg.status})
                        </option>)}
                    </select>
                  </div>
                  <div className="space-y-2" data-id="element-560">
                    <label className="text-sm font-medium text-slate-300" data-id="element-561">
                      Register Price ($) *
                    </label>
                    <input name="registerPrice" type="number" step="0.01" defaultValue={editingTld?.registerPrice} required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-562" />
                  </div>
                  <div className="space-y-2" data-id="element-563">
                    <label className="text-sm font-medium text-slate-300" data-id="element-564">
                      Renew Price ($) *
                    </label>
                    <input name="renewPrice" type="number" step="0.01" defaultValue={editingTld?.renewPrice} required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-565" />
                  </div>
                  <div className="space-y-2" data-id="element-566">
                    <label className="text-sm font-medium text-slate-300" data-id="element-567">
                      Transfer Price ($) *
                    </label>
                    <input name="transferPrice" type="number" step="0.01" defaultValue={editingTld?.transferPrice} required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-568" />
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-slate-800" data-id="element-569">
                  <label className="flex items-center gap-3 cursor-pointer" data-id="element-570">
                    <input type="checkbox" name="whoisPrivacy" defaultChecked={editingTld ? editingTld.whoisPrivacy : true} className="w-5 h-5 rounded border-slate-700 bg-slate-950 text-fox-600 focus:ring-fox-500" data-id="element-571" />
                    <div data-id="element-572">
                      <span className="text-white block" data-id="element-573">
                        Free WHOIS Privacy
                      </span>
                      <span className="text-xs text-slate-500" data-id="element-574">
                        Include privacy protection for free with this TLD
                      </span>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer" data-id="element-575">
                    <input type="checkbox" name="active" defaultChecked={editingTld ? editingTld.active : true} className="w-5 h-5 rounded border-slate-700 bg-slate-950 text-fox-600 focus:ring-fox-500" data-id="element-576" />
                    <span className="text-white" data-id="element-577">
                      Active (Available for registration)
                    </span>
                  </label>
                </div>

                <div className="pt-4 border-t border-slate-800 flex justify-end gap-3" data-id="element-578">
                  <button type="button" onClick={() => setIsTldModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors" data-id="element-579">
                    Cancel
                  </button>
                  <button type="submit" className="bg-fox-600 hover:bg-fox-500 text-white px-6 py-2 rounded-lg font-medium transition-colors" data-id="element-580">
                    Save TLD
                  </button>
                </div>
              </form>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Registrar Modal */}
      <AnimatePresence data-id="element-581">
        {isRegModalOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-582">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-583">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10" data-id="element-584">
                <h2 className="text-xl font-bold text-white" data-id="element-585">
                  {editingReg ? 'Edit Registrar' : 'Add Registrar Connection'}
                </h2>
                <button onClick={() => setIsRegModalOpen(false)} className="text-slate-400 hover:text-white" data-id="element-586">
                  <X size={24} data-id="element-587" />
                </button>
              </div>
              <form onSubmit={handleSaveReg} className="p-6 space-y-6" data-id="element-588">
                <div className="space-y-4" data-id="element-589">
                  <div className="space-y-2" data-id="element-590">
                    <label className="text-sm font-medium text-slate-300" data-id="element-591">
                      Registrar Name *
                    </label>
                    <input name="name" defaultValue={editingReg?.name} required placeholder="e.g. eNom, Namecheap, ResellerClub" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-592" />
                  </div>
                  <div className="space-y-2" data-id="element-593">
                    <label className="text-sm font-medium text-slate-300" data-id="element-594">
                      API Endpoint URL *
                    </label>
                    <input name="apiEndpoint" type="url" defaultValue={editingReg?.apiEndpoint} required placeholder="https://api.registrar.com/v1" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-595" />
                  </div>
                  <div className="space-y-2" data-id="element-596">
                    <label className="text-sm font-medium text-slate-300" data-id="element-597">
                      API Key / Token *
                    </label>
                    <input name="apiKey" type="password" defaultValue={editingReg?.apiKey} required placeholder="••••••••••••••••" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-598" />
                  </div>
                  <div className="space-y-2" data-id="element-599">
                    <label className="text-sm font-medium text-slate-300" data-id="element-600">
                      Supported TLDs (comma separated) *
                    </label>
                    <textarea name="supportedTlds" defaultValue={editingReg?.supportedTlds.join(', ')} required rows={3} placeholder=".com, .net, .org, .io" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none resize-none" data-id="element-601" />
                  </div>
                </div>

                {/* Test Connection */}
                <div className="bg-slate-950 border border-slate-800 rounded-xl p-4" data-id="element-602">
                  <div className="flex items-center justify-between" data-id="element-603">
                    <div data-id="element-604">
                      <h4 className="text-white font-medium text-sm" data-id="element-605">
                        Test API Connection
                      </h4>
                      <p className="text-slate-500 text-xs" data-id="element-606">
                        Verify credentials before saving
                      </p>
                    </div>
                    <button type="button" onClick={handleTestReg} disabled={isTestingReg} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-607">
                      {isTestingReg ? <Loader2 size={14} className="animate-spin" data-id="element-608" /> : <Wifi size={14} data-id="element-609" />}
                      {isTestingReg ? 'Testing...' : 'Test Connection'}
                    </button>
                  </div>
                  {testResult && <motion.div initial={{
                opacity: 0,
                y: -5
              }} animate={{
                opacity: 1,
                y: 0
              }} className={`mt-3 p-3 rounded-lg flex items-center gap-2 text-sm ${testResult === 'success' ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'}`} data-id="element-610">
                      {testResult === 'success' ? <CheckCircle size={16} data-id="element-611" /> : <AlertCircle size={16} data-id="element-612" />}
                      {testResult === 'success' ? 'Connection successful! API is reachable.' : 'Connection failed. Please check your credentials.'}
                    </motion.div>}
                </div>

                <div className="pt-4 border-t border-slate-800 flex justify-end gap-3" data-id="element-613">
                  <button type="button" onClick={() => setIsRegModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors" data-id="element-614">
                    Cancel
                  </button>
                  <button type="submit" className="bg-fox-600 hover:bg-fox-500 text-white px-6 py-2 rounded-lg font-medium transition-colors" data-id="element-615">
                    Save Registrar
                  </button>
                </div>
              </form>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}